// The ONE typed API client for the new admin SPA. Every fetch lives here so
// reconciling field-name drift with the sibling permissions PR (grantedBy vs
// granted_by, createdAt vs created_at) is a one-file change — see `normalizeGrant`.
//
// Identity: the stub `X-Employee-Email` header (local/preview) + `credentials:
// "include"` (the signed Google `admin_authority` cookie in cloud). The backend
// gate is `require_employee`: a caller with no identity → 401, a signed-in
// non-employee → 403. Area-scoped routes may additionally 403 when the caller
// lacks the area grant; pages surface that as the "access required" panel.
//
// Permissions and customer-support transport shapes are generated from backend
// Pydantic models. The remaining legacy admin shapes are local until those routes
// move onto declared response models.

import { type BudgetRange, EMPTY_BUDGET_RANGE } from "./budget-range";
import { ADMIN_API_BASE, EMPLOYEE_EMAIL } from "../config";
import { appendDateRange, type DateRange } from "./date-range";
import type { ExperimentScope } from "./experiment-scope";
import type { ProjectBudget } from "./format";
import type {
  AdminExportResponse,
  AdminFunnelRangeResponse,
  AdminFunnelResponse,
  AdminSearchPick,
  AbPresetWindow,
  AdminAbTestsEnvelope,
  AdminAllocationResponse,
  AdminArmProjectsResponse,
  AdminExperimentDecision,
  AdminExperimentDetail,
  AdminExperimentIssuesResponse,
  AdminGuardrailThresholdsResponse,
  AdminProjectArmsResponse,
  AdminTagStateResponse,
  AdminTagVersionsResponse,
  DecideExperimentRequest,
  KillExperimentRequest,
  RestoreDefaultsRequest,
  RestoreDefaultsResponse,
  SetGuardrailThresholdsRequest,
  SetTagStateRequest,
  AdminExperimentsResponse,
  AdminExperimentationResponse,
  AdminInjectionDials,
  AdminLifecycleResponse,
  AdminProjectExperimentsResponse,
  AdminTriggersResponse,
  SetAllocationRequest,
  SetInjectionDialsRequest,
  AdminBudgetThresholdsResponse,
  AdminPromptPreview,
  AdminPromptSectionsResponse,
  AdminPromptText,
  AdminVersionCohortsResponse,
  AdminVersionCohortsSliceResponse,
  AdminShortlistTimeframeResponse,
  TimeframeWindow,
  AdminCountriesResponse,
  AgentControlRequest,
  AgentRunsResponse,
  AreaGrantsResponse,
  ChatMessage,
  ConversationTranslateAck,
  ConversationTranslation,
  DebugChatAck,
  DebugChatRequest,
  DebugProjectsResponse,
  DebugTurnsResponse,
  CohortWindow,
  FunnelPresetWindow,
  GrantAreasRequest,
  GrantAreasResponse,
  LaunchDashboardArmResponse,
  LaunchDashboardResponse,
  LaunchDrilldownMetric,
  LaunchDrilldownResponse,
  LaunchDrilldownWindow,
  LaunchRedeemResponse,
  LaunchRemoteLinkResponse,
  MyAreasResponse,
  OperatorMessageRequest,
  PlatformKillRequest,
  PlatformKillStatus,
  PlatformResumeRequest,
  RevokeGrantResponse,
  SavedReplyCreateRequest,
  SupportAttentionResponse,
  SupportConversationResponse,
  SupportOverviewResponse,
  SupportPeopleResponse,
  SupportPersonDetail,
  SupportProjectsResponse,
  SupportStatsResponse,
  TakeoverRequest,
  TalentQueryResponse,
  TalentQuerySelection,
  TurnRunsResponse,
  AdminDeclinesResponse,
  AdminOffersResponse,
  AdminSearchDetail,
  AdminSearchesResponse,
  VipListResponse,
  VipSellerRow,
  VipAddRequest,
  VipUpdateRequest,
  ExcludedListResponse,
  ExcludeUpsertRequest,
  OptoutListResponse,
  OptoutAddRequest,
  SellerPreferenceEditRequest,
  SellerPreferenceReviewRequest,
  SellerPreferencesResponse,
  SellerPreferenceStateRequest,
  TalentOptoutRequestsResponse,
  TalentOptoutResolveRequest,
  TalentOkResponse,
  ProjectExport,
  ImportProjectResponse,
  ShortlistAdditionRow,
  ShortlistListResponse,
  ShortlistAddRequest,
} from "@recruiter/shared/types";
import {
  type Access,
  type Area,
  type AreaAccess,
  parseAccess,
  parseAreas,
  parseGrantedAreas,
} from "./areas";
import { clearSignedOut, storeEmployeeEmail } from "./identity";

// --- Errors -----------------------------------------------------------------

/** A typed transport error. `status` is the HTTP status, or "network" when the
 *  request never completed. `detail` is the backend `{detail}` message when present,
 *  and `code` the stable `error` key of a STRUCTURED refusal (`area_forbidden`,
 *  `admin_signin_required`, `not_employee`, ...) — the status alone cannot say
 *  whether a 403 is recoverable, and the panels branch on that. */
export class ApiError extends Error {
  readonly status: number | "network";
  readonly detail: string;
  readonly code: string | null;
  constructor(status: number | "network", detail: string, code: string | null = null) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
  /** Signed in but lacking access (not an employee, or missing the area grant). */
  get isForbidden(): boolean {
    return this.status === 403;
  }
  /** Not signed in. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

// --- Core fetch -------------------------------------------------------------

/** The one authed request: identity headers, `credentials: "include"`, and every
 *  transport/HTTP failure normalized to `ApiError`. Returns the raw `Response` so
 *  a BINARY route (a file download) can take `.blob()` where JSON routes take
 *  `.json()` — the auth and error handling must not be re-implemented per body
 *  type, which is how one of them ends up not raising `ApiError`. */
async function send(path: string, init: RequestInit = {}, accept = "application/json") {
  const headers = new Headers(init.headers);
  headers.set("accept", accept);
  // Stub identity — sent (and honored) in stub-mode backends; ignored in cloud
  // where the signed authority cookie on the same-origin request is authoritative.
  if (EMPLOYEE_EMAIL && !headers.has("x-employee-email")) {
    headers.set("x-employee-email", EMPLOYEE_EMAIL);
  }

  let resp: Response;
  try {
    resp = await fetch(`${ADMIN_API_BASE}${path}`, {
      ...init,
      headers,
      credentials: "include",
    });
  } catch (e) {
    throw new ApiError("network", (e as Error).message || "Network request failed.");
  }

  if (!resp.ok) {
    let detail = `Request failed (${resp.status})`;
    let code: string | null = null;
    try {
      const body = (await resp.json()) as { detail?: unknown };
      if (body && typeof body.detail === "string") detail = body.detail;
      // A STRUCTURED detail: several backend refusals raise
      // `detail={"code": ..., "message": ...}` so the SPA can branch on a stable
      // code (the platform-kill 503, the human-takeover 409 — the
      // shape every typed refusal shares). Without this the whole body was
      // dropped and the toast read "Request failed (409)", which tells nobody
      // anything.
      else if (body && typeof body.detail === "object" && body.detail !== null) {
        const message = (body.detail as { message?: unknown }).message;
        if (typeof message === "string" && message) detail = message;
        // `error` is the stable branch key (the backend spells the code under
        // `error`, the prose under `message`). Kept beside `detail` rather than
        // parsed out of the sentence, which is copy and may be reworded.
        const error = (body.detail as { error?: unknown }).error;
        if (typeof error === "string" && error) code = error;
      }
    } catch {
      // Non-JSON error body — keep the generic detail.
    }
    throw new ApiError(resp.status, detail, code);
  }
  return resp;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const resp = await send(path, init);
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/** Read a binary route's body. Accepts ANY content type, because the response is a
 *  document of whatever type the client uploaded, never JSON. */
async function blobRequest(path: string): Promise<Blob> {
  return (await send(path, {}, "*/*")).blob();
}

function jsonBody(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  };
}

// --- Identity ---------------------------------------------------------------

export interface AdminMe {
  email: string | null;
}

/** Boot identity check. 401 → sign-in panel; 403 → not-an-employee panel. On
 *  success caches the real email (cloud) so the shell shows it. */
export async function fetchMe(): Promise<AdminMe> {
  const data = await request<AdminMe>("/api/admin/me");
  if (data.email) {
    storeEmployeeEmail(data.email);
    clearSignedOut();
  }
  return data;
}

// --- Permissions ------------------------------------------------------------
// The transport shapes below are generated from backend Pydantic models. Grant
// rows are normalized into the camelCase view type the components render.

export interface Grant {
  email: string;
  area: Area;
  /** How much of the area this grant carries (migration 0120). */
  access: Access;
  grantedBy: string | null;
  createdAt: string | null;
}

/** Normalize one raw grant row. The contract is snake_case (`granted_by`,
 *  `created_at`); camelCase is tolerated defensively. Rows whose `area` isn't a
 *  recognized area are dropped by the caller (see `fetchPermissions`); an
 *  unrecognized `access` narrows to `read` rather than dropping the row, because
 *  a grant this console cannot fully describe is still a grant it must show —
 *  see `parseAccess`.
 *
 *  An ABSENT `access` is different from an unrecognized one, and reads as `full`.
 *  A row with no level at all comes from a backend that predates 0120, where
 *  every grant meant full access; narrowing it to `read` would label every person
 *  in the table "read only" during the deploy window, which is a lie about who
 *  can act. Same rule as `parseGrantedAreas` applies to the bare-name shape, and
 *  it retires with it. */
export function normalizeGrant(raw: Record<string, unknown>): Grant | null {
  const area = typeof raw.area === "string" ? raw.area : "";
  const parsed = parseAreas([area]);
  if (parsed.length === 0) return null;
  const grantedBy = raw.granted_by ?? raw.grantedBy ?? null;
  const createdAt = raw.created_at ?? raw.createdAt ?? null;
  return {
    email: typeof raw.email === "string" ? raw.email : "",
    area: parsed[0],
    access: raw.access === undefined ? "full" : parseAccess(raw.access),
    grantedBy: typeof grantedBy === "string" ? grantedBy : null,
    createdAt: typeof createdAt === "string" ? createdAt : null,
  };
}

/** What `/permissions/me` tells this console about itself. */
export interface MyAccess {
  /** The caller's granted areas and the level held on each. */
  areas: AreaAccess[];
  /**
   * Does the SERVER on the other end understand levels at all?
   *
   * The one signal that stops the grant form over-granting during a deploy. The
   * admin SPA is static assets on GCS+CDN and the backend is Cloud Run, and the
   * two deploy in PARALLEL (`deploy-backend-main` and `deploy-frontend` are
   * siblings in both workflows) — with the frontend's build-and-publish routinely
   * beating the backend's build → migrate → roll. So a new console talking to an
   * old backend is not a corner case, it is the expected state for a few minutes.
   *
   * That matters because the write path fails PERMISSIVE. `GrantAreasRequest`
   * predates `access` and sets no `model_config`, so pydantic drops the unknown
   * key: an admin picks Read only, the server stores FULL, returns 200, and
   * nothing anywhere says so. Detected here off the response SHAPE — a levelled
   * backend answers `grants`, an older one only `areas` — so the form can decline
   * to offer a level it cannot actually deliver.
   */
  levelsSupported: boolean;
}

/** The CALLER's own granted areas AND the level held on each — drives sidebar
 *  visibility, route gating, and whether a page draws its write controls.
 *
 *  Reads `grants` (the `[{area, access}]` contract) and falls back to the
 *  pre-0120 bare-name `areas` when a backend that predates levels is on the other
 *  end — the browser is the one client we cannot deploy atomically with the
 *  server, so this bundle has to survive meeting either one. `parseGrantedAreas`
 *  takes both shapes; both fields disappear together once the old bundles have
 *  aged out, and `levelsSupported` goes with them. */
export async function fetchMyAreas(): Promise<MyAccess> {
  const data = await request<MyAreasResponse>("/api/admin/permissions/me");
  // Key PRESENCE, not truthiness: a levelled backend answers `grants: []` for an
  // employee who holds nothing, and that is still a levelled backend.
  const levelsSupported = data.grants !== undefined;
  return {
    areas: parseGrantedAreas(levelsSupported ? data.grants : data.areas),
    levelsSupported,
  };
}

/** Every grant in the system (AreaGrantsResponse). 403 without the
 *  system_admin area — surfaced as the access-required panel. */
export async function fetchPermissions(): Promise<Grant[]> {
  const data = await request<AreaGrantsResponse>("/api/admin/permissions");
  const rows = Array.isArray(data.grants) ? data.grants : [];
  const out: Grant[] = [];
  for (const row of rows) {
    if (row && typeof row === "object") {
      const g = normalizeGrant(row as Record<string, unknown>);
      if (g) out.push(g);
    }
  }
  return out;
}

/** Bulk-grant `areas` to every email in `emails`, all at `access`. An UPSERT:
 *  granting an area someone already holds at the OTHER level MOVES them to this
 *  one, which is how the editor switches a person between Read only and Full
 *  access without a revoke-then-grant that would briefly leave them holding
 *  nothing. Returns the count of rows CHANGED (GrantAreasResponse). Emails are
 *  lowercased server-side; malformed emails → 422.
 *
 *  One level per call, so a save that mixes levels sends one call per level. */
export function addPermissions(
  emails: string[],
  areas: Area[],
  access: Access,
): Promise<GrantAreasResponse> {
  const body: GrantAreasRequest = { emails, areas, access };
  return request("/api/admin/permissions", jsonBody(body));
}

/** Revoke one (email, area) grant, at whatever level it was held
 *  (RevokeGrantResponse). Dropping someone to Read only is an `addPermissions`
 *  call, not this. */
export function revokePermission(email: string, area: Area): Promise<RevokeGrantResponse> {
  return request(
    `/api/admin/permissions/${encodeURIComponent(email)}/${encodeURIComponent(area)}`,
    { method: "DELETE" },
  );
}

// --- Platform stop-loss: System Admin ---------------------------------------

/** The current Big Red Button state. The server bypasses its short cache so an
 * operator always sees the Postgres truth before engaging or resuming Mira. */
export function fetchPlatformKillStatus(): Promise<PlatformKillStatus> {
  return request<PlatformKillStatus>("/api/admin/platform/status");
}

/** Engage the platform-wide stop-loss after the operator has supplied the
 * mandatory incident reason and exact confirmation. */
export function engagePlatformKill(body: PlatformKillRequest): Promise<PlatformKillStatus> {
  return request<PlatformKillStatus>("/api/admin/platform/kill", jsonBody(body));
}

/** Resume the platform after the operator has supplied the exact confirmation. */
export function resumePlatformKill(body: PlatformResumeRequest): Promise<PlatformKillStatus> {
  return request<PlatformKillStatus>("/api/admin/platform/resume", jsonBody(body));
}

// --- Customer support: area-isolated reader ---------------------------------

export function fetchSupportOverview(): Promise<SupportOverviewResponse> {
  return request<SupportOverviewResponse>("/api/admin/support/overview");
}

export function fetchSupportStats(): Promise<SupportStatsResponse> {
  return request<SupportStatsResponse>("/api/admin/support/stats");
}

export function fetchSupportAttention(): Promise<SupportAttentionResponse> {
  return request<SupportAttentionResponse>("/api/admin/support/attention");
}

export function fetchSupportProjects(
  q: string,
  cursor: string | null,
  range?: DateRange,
  scope?: ExperimentScope,
): Promise<SupportProjectsResponse> {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (cursor) params.set("cursor", cursor);
  appendDateRange(params, range);
  appendExperimentScope(params, scope);
  return request<SupportProjectsResponse>(`/api/admin/support/projects?${params.toString()}`);
}

export function searchPeople(
  q: string,
  range?: DateRange,
  scope?: ExperimentScope,
): Promise<SupportPeopleResponse> {
  // `q` keeps its `encodeURIComponent` spelling rather than moving into the
  // URLSearchParams below. Both are decoded identically by the backend, but
  // that helper percent-encodes a space as `+`, and changing the bytes this
  // route sends is not something a date filter should quietly do (api.test.ts
  // pins the encoding).
  const params = new URLSearchParams();
  appendDateRange(params, range);
  appendExperimentScope(params, scope);
  const rest = params.toString();
  return request<SupportPeopleResponse>(
    `/api/admin/support/people?q=${encodeURIComponent(q)}${rest ? `&${rest}` : ""}`,
  );
}

export function fetchPerson(id: string): Promise<SupportPersonDetail> {
  return request<SupportPersonDetail>(`/api/admin/support/people/${encodeURIComponent(id)}`);
}

export function fetchConversation(projectId: string): Promise<SupportConversationResponse> {
  return request<SupportConversationResponse>(
    `/api/admin/support/conversations/${encodeURIComponent(projectId)}`,
  );
}

/** One of the conversation's uploaded files, as bytes — the CS-gated download.
 *
 *  The support twin of `fetchExplorerProjectFile`, and separate for the same
 *  reason the payloads are: a rep is pointed at `/api/admin/support/*`, and the
 *  talent-gated route would 403 them (lib/explorer-access.ts). */
export function fetchSupportProjectFile(projectId: string, fileId: string): Promise<Blob> {
  return blobRequest(
    `/api/admin/support/conversations/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`,
  );
}

// --- Talent management: the BI snapshot -------------------------------------

/** Ask the warehouse one SELECTION: range, filters, order, page.
 *
 *  A POST because the selection is a structured body, not because it writes
 *  anything — it is a read, gated at `talent_management` READ level, and it is
 *  safe to replay. The backend does one authorization check, one cache read and
 *  at most one enqueue; it never queries BigQuery on the request cycle (see
 *  backend/recruiter/talent).
 *
 *  `status` is typed degradation, not an error:
 *   - `ready`   — `result` is the answer.
 *   - `pending` — the query is running. The answer lands on `channel` (Pusher) and
 *                 is then readable via `fetchTalentResult`. Do NOT poll for it.
 *   - `unavailable` — nothing to show; `detail` says why. */
export function queryTalentMetrics(selection: TalentQuerySelection): Promise<TalentQueryResponse> {
  return request<TalentQueryResponse>("/api/admin/talent/metrics/query", jsonBody(selection));
}

/** Read one answered selection by the key the `pending` response handed back.
 *
 *  Called ONCE, after the Pusher event says the answer landed — never on a timer.
 *  A `pending` response is not a cue to poll; that is the whole reason the event
 *  exists (doctrine 1.2). */
export function fetchTalentResult(resultKey: string): Promise<TalentQueryResponse> {
  return request<TalentQueryResponse>(
    `/api/admin/talent/metrics/results/${encodeURIComponent(resultKey)}`,
  );
}

// --- Curated talent lists (talent_management area): VIP / Excluded / Opt-out ---

export function fetchVipSellers(): Promise<VipListResponse> {
  return request<VipListResponse>("/api/admin/talent/vip");
}
export function addVipSeller(body: VipAddRequest): Promise<VipSellerRow> {
  return request<VipSellerRow>("/api/admin/talent/vip", jsonBody(body));
}
export function updateVipSeller(id: number, body: VipUpdateRequest): Promise<VipSellerRow> {
  return request<VipSellerRow>(`/api/admin/talent/vip/${id}`, {
    ...jsonBody(body),
    method: "PATCH",
  });
}
export function deactivateVipSeller(id: number): Promise<TalentOkResponse> {
  return request<TalentOkResponse>(`/api/admin/talent/vip/${id}`, { method: "DELETE" });
}

export function fetchExcluded(): Promise<ExcludedListResponse> {
  return request<ExcludedListResponse>("/api/admin/talent/excluded");
}
export function upsertExcluded(body: ExcludeUpsertRequest): Promise<TalentOkResponse> {
  return request<TalentOkResponse>("/api/admin/talent/excluded", {
    ...jsonBody(body),
    method: "PUT",
  });
}
export function removeExcluded(sellerId: string): Promise<TalentOkResponse> {
  return request<TalentOkResponse>(`/api/admin/talent/excluded/${encodeURIComponent(sellerId)}`, {
    method: "DELETE",
  });
}

export function fetchOptout(): Promise<OptoutListResponse> {
  return request<OptoutListResponse>("/api/admin/talent/optout");
}
export function addOptout(body: OptoutAddRequest): Promise<TalentOkResponse> {
  return request<TalentOkResponse>("/api/admin/talent/optout", jsonBody(body));
}
export function removeOptout(sellerId: string): Promise<TalentOkResponse> {
  return request<TalentOkResponse>(`/api/admin/talent/optout/${encodeURIComponent(sellerId)}`, {
    method: "DELETE",
  });
}

/** Recent search runs, newest first (keyset-paged on `cursor`).
 *
 *  The OPERATIONAL lane: per-run product truth, deliberately not the BI snapshot.
 *  Nothing here is an aggregate. */
/** The Searches lane's server-side narrowing: surface, outcome (`degraded` |
 *  `ok`), the project's work shape (`project` | `ongoing` | `hybrid` | `none`),
 *  and the free-text search, all applied in SQL across the whole feed. */
export interface SearchesFilter {
  surfaces?: readonly string[];
  outcomes?: readonly string[];
  shapes?: readonly string[];
  /** The project's creation stamps (`projects.created_device` / `origin_country`),
   *  each with the sentinel `unknown` for a project carrying none. Empty = all. */
  devices?: readonly string[];
  countries?: readonly string[];
  q?: string;
  /** The cohort range, applied in SQL like every other filter here so it
   *  narrows the WHOLE lane rather than the pages already fetched. */
  range?: DateRange;
}

export function fetchSearches(
  cursor: string | null,
  filter: SearchesFilter = {},
): Promise<AdminSearchesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  for (const surface of filter.surfaces ?? []) params.append("surfaces", surface);
  for (const outcome of filter.outcomes ?? []) params.append("outcomes", outcome);
  for (const shape of filter.shapes ?? []) params.append("shapes", shape);
  for (const device of filter.devices ?? []) params.append("devices", device);
  for (const country of filter.countries ?? []) params.append("countries", country);
  if (filter.q) params.set("q", filter.q);
  appendDateRange(params, filter.range);
  const qs = params.toString();
  return request<AdminSearchesResponse>(`/api/admin/talent/searches${qs ? `?${qs}` : ""}`);
}

/** A keyset page of recent offers across every project (the Offers tab).
 *  De-anonymized (seller, price, JUNO's fit score) behind the talent gate. */
/** The Offers lane's server-side narrowing: recommendation state (`yes` |
 *  `no`; both selected filters nothing) and currency, applied in SQL across
 *  the whole lane. */
export interface OffersFilter {
  recommended?: readonly string[];
  currencies?: readonly string[];
  /** The cohort range, applied in SQL like every other filter here so it
   *  narrows the WHOLE lane rather than the pages already fetched. */
  range?: DateRange;
}

export function fetchOffers(
  cursor: string | null,
  filter: OffersFilter = {},
): Promise<AdminOffersResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  for (const value of filter.recommended ?? []) params.append("recommended", value);
  for (const currency of filter.currencies ?? []) params.append("currencies", currency);
  appendDateRange(params, filter.range);
  const qs = params.toString();
  return request<AdminOffersResponse>(`/api/admin/talent/offers${qs ? `?${qs}` : ""}`);
}

/** A keyset page of recent DECLINES across every project (the Declines tab).
 *
 *  The operational lane again: one row per declined thread, carrying the coarse
 *  reason, the talent's verbatim words, and whether they declined us or we declined
 *  them. No breakdown or rate by reason is served or derived — that shape is a
 *  metric, and metrics come from curated BigQuery, never from summing these rows. */
/** The Declines lane's server-side narrowing: reason (the `NO_VALUE` sentinel
 *  selects rows with nothing recorded), who declined, and whether the talent
 *  ever replied — applied in SQL across the whole lane. */
export interface DeclinesFilter {
  reasons?: readonly string[];
  who?: readonly string[];
  replied?: readonly string[];
  /** The cohort range, applied in SQL like every other filter here so it
   *  narrows the WHOLE lane rather than the pages already fetched. */
  range?: DateRange;
}

export function fetchDeclines(
  cursor: string | null,
  filter: DeclinesFilter = {},
): Promise<AdminDeclinesResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  for (const reason of filter.reasons ?? []) params.append("reasons", reason);
  for (const value of filter.who ?? []) params.append("who", value);
  for (const value of filter.replied ?? []) params.append("replied", value);
  appendDateRange(params, filter.range);
  const qs = params.toString();
  return request<AdminDeclinesResponse>(`/api/admin/talent/declines${qs ? `?${qs}` : ""}`);
}

/** Talents who ASKED to stop being contacted, open requests first.
 *  A row here suppresses nobody; `resolveOptoutRequest` is what does. */
export function fetchOptoutRequests(): Promise<TalentOptoutRequestsResponse> {
  return request<TalentOptoutRequestsResponse>("/api/admin/talent/optout-requests");
}

/** Honour one talent's request: add them to the opt-out list, then stamp every
 *  open request of theirs handled. Server-side the suppression happens FIRST, so a
 *  partial failure leaves the row in the queue rather than silently dropping it. */
export function resolveOptoutRequest(body: TalentOptoutResolveRequest): Promise<TalentOkResponse> {
  return request<TalentOkResponse>("/api/admin/talent/optout-requests/resolve", jsonBody(body));
}

/** ONE PAGE of the standing talent preferences awaiting an operator (migration 0175).
 *  NOTHING in this queue filters a search: a preference starts removing the talent from
 *  briefs only once `reviewSellerPreference` approves it. Pass a status to read the decided
 *  rows instead ("active" is what is live now, "rejected" is what we turned down).
 *
 *  Keyset-paged: pass the previous response's `nextCursor` to read what is behind this page,
 *  and stop when it comes back null. It used to be a single capped list with no cursor, so a
 *  backlog past the ceiling was unreachable from the console entirely. */
export function fetchSellerPreferences(
  status?: string,
  cursor?: string | null,
): Promise<SellerPreferencesResponse> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (cursor) params.set("cursor", cursor);
  const q = params.size > 0 ? `?${params.toString()}` : "";
  return request<SellerPreferencesResponse>(`/api/admin/talent/seller-preferences${q}`);
}

/** Approve or reject one queued preference. Approving is the click that makes it filter;
 *  rejecting leaves whatever the talent already had active untouched, because a rejected
 *  proposal is not a request to drop the boundary they had before. */
export function reviewSellerPreference(
  body: SellerPreferenceReviewRequest,
): Promise<TalentOkResponse> {
  return request<TalentOkResponse>("/api/admin/talent/seller-preferences/review", jsonBody(body));
}

/** Correct one QUEUED capture before ruling on it. Every field REPLACES what is stored, so
 *  the form has to submit the whole row: a cleared field is how an operator says the talent
 *  never stated that boundary. Awaiting-review rows only - a row in any other state answers
 *  404, and an impossible working-hours window answers 422. */
export function editSellerPreference(body: SellerPreferenceEditRequest): Promise<TalentOkResponse> {
  return request<TalentOkResponse>("/api/admin/talent/seller-preferences/edit", jsonBody(body));
}

/** Reverse an earlier ruling on a DECIDED preference. `unapprove` takes a live one off
 *  (it moves to the rejected list, nothing replaces it); `reapprove` makes a rejected one
 *  the talent's single live preference again, superseding whatever they currently have
 *  live. A row not in the action's starting state answers 404, so a double-click or a
 *  racing operator gets a refusal, never a silent ok. */
export function setSellerPreferenceState(
  body: SellerPreferenceStateRequest,
): Promise<TalentOkResponse> {
  return request<TalentOkResponse>("/api/admin/talent/seller-preferences/state", jsonBody(body));
}

/** One search run: SCOUT's summary, the preferences, and the ranked shortlist
 *  with SCOUT's verbatim per-talent reasoning. 404 → ApiError(404). */
export function fetchSearchDetail(id: string): Promise<AdminSearchDetail> {
  return request<AdminSearchDetail>(`/api/admin/talent/searches/${encodeURIComponent(id)}`);
}

// --- User administration: users ---------------------------------------------

export interface AdminUserRow {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: string | null;
  lastActiveAt: string | null;
  projectCount: number;
  topLevelProjectCount: number;
  /** True when the account is blocked (users.blocked_at set, migration 0084). */
  blocked: boolean;
  /** The regional model tier the account was first seen on (`luna` | `terra`,
   *  migration 0195) and the browser-detected country it was decided from. Null
   *  until a request carried a country; optional on an older backend. */
  modelTier?: string | null;
  originCountry?: string | null;
}

export interface AdminUsersResponse {
  users: AdminUserRow[];
  /** Null on the last page. Keyset-paged at 200 a page, for the same reason the
   *  projects listing is: the unbounded read this replaced fanned the whole
   *  message table out across a join to compute one timestamp per user. */
  nextCursor: string | null;
}

// The users list is fetched by `fetchExplorerUsers` below — there is ONE
// fetcher for `/api/admin/users`. (There were two identical ones while User
// Administration had its own page; that page is gone, and so is its copy.)

/** Permanently delete a user + everything tied to them (the operator-side DSAR
 *  erase). IRREVERSIBLE — callers MUST confirm first. Requires the
 *  user_administration area (403 otherwise → access-required panel / toast). */
export function deleteUser(userId: string): Promise<{ deleted: boolean; user_id: string }> {
  return request(`/api/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
}

/** Block or unblock a user account — an idempotent toggle (the per-user analogue
 *  of the per-project pause; users.blocked_at, migration 0084). Requires the
 *  user_administration area (403 otherwise → toast). Returns the new state. */
export function setUserBlock(
  userId: string,
  blocked: boolean,
): Promise<{ userId: string; blocked: boolean }> {
  return request(`/api/admin/users/${encodeURIComponent(userId)}/block`, jsonBody({ blocked }));
}

// --- Explorer: the unified user / project view -------------------------------
//
// These read the LEGACY `/api/admin/{users,projects}` routes, whose responses are
// `to_wire()` dicts rather than declared Pydantic models — so unlike the support
// and permissions shapes above, they are hand-mirrored here (same rule the file
// header states). Keep them in step with backend/web/routes/admin.py; they are
// deliberately identical to admin-internal's `admin-client.ts` declarations so a
// backend change lands in two known places rather than drifting apart silently.

/** Per-project content tallies.
 *
 *  The CROSS-USER list (`GET /api/admin/projects`) returns only these three;
 *  the per-user list inside a user's detail also returns `sections` and
 *  `searchRuns`. Both are typed here with the extras OPTIONAL rather than as two
 *  near-identical interfaces, and the UI hides a column whose value is absent
 *  instead of printing a confident zero for a count nobody sent. */
export interface ExplorerProjectCounts {
  blocks: number;
  files: number;
  /** The CLIENT's own messages, not the transcript length — the backend counts
   *  `role='user'` rows only. It used to count every row, so Mira's replies, her
   *  widgets, and the internal partner notes all inflated it and a client who
   *  sent two lines looked chatty. */
  messages: number;
  sections?: number;
  searchRuns?: number;
}

/** One project row. Mirrors `list_all_projects_admin` /
 *  `list_all_projects_for_user_admin` — `name` is `text NOT NULL` in Postgres
 *  but is frequently the EMPTY STRING (a project is created before Mira names
 *  it), so never render it raw: use `projectTitle` (lib/project-title.ts). */
export interface ExplorerProjectRow {
  id: string;
  name: string;
  /** `projects.status`. Reads 'draft' on essentially every real row (issue
   *  #461), so it is NOT the badge: `stage` is. Kept because the field is on the
   *  wire and dropping it would hide the day it starts meaning something. */
  status: string | null;
  /** How far the project actually got — the console's status badge. Derived by
   *  `recruiter/repo/project_stage.py` from what the project produced. Typed as
   *  a plain string because the wire union is OPEN (a newer backend may send a
   *  rung this bundle predates); `lib/project-stage.ts` narrows it. */
  stage: string | null;
  kind: string | null;
  /** The engagement SHAPE the agents latched ("project" / "ongoing" /
   *  "hybrid") — the work-shape tag + filter (`lib/work-shape.ts`). Null = not
   *  classified yet; optional because this route's payload is a hand-mirrored
   *  dict and an older backend simply omits it. */
  workShape?: string | null;
  /** WHO is paying ("smb" / "solo_professional" / "individual_consumer" /
   *  "enterprise" / "unclear") — the buyer-type tag + filter
   *  (`lib/buyer-type.ts`). Resolved from Fiverr's own `company_size` where the
   *  account carries one, from the per-turn conversation read otherwise. Null =
   *  nothing has classified it yet; optional for the same reason as `workShape`
   *  above, since this route's payload is a hand-mirrored dict. */
  buyerType?: string | null;
  parentProjectId: string | null;
  summary: string | null;
  /** What the project was CREATED on, bucketed from the creating request's
   *  User-Agent (migration 0183, `lib/device.ts` mirrors the vocabulary). Null
   *  where nothing was recorded: a project imported, accepted from a suggestion,
   *  born on the MCP surface, or created before 0183 — most of the table until
   *  that migration has been shipped a while. Optional because this route's
   *  payload is a hand-mirrored dict and an older backend simply omits them. */
  createdDevice?: string | null;
  createdOs?: string | null;
  createdBrowser?: string | null;
  /** The regional model tier the project was CREATED on (`luna` | `terra`,
   *  migration 0195; `lib/model-tier.ts` mirrors the vocabulary) and the
   *  browser-detected country it was decided from. Null on rows older than the
   *  migration; optional because an older backend simply omits them. */
  modelTier?: string | null;
  originCountry?: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  counts: ExplorerProjectCounts;
  /** Present on the cross-user list, absent inside a user's own detail (where
   *  the owner is the page you are already on). */
  owner?: {
    id: string;
    email: string | null;
    displayName: string | null;
  };
  /** What the client COMMITTED to spend, straight off `search_preferences` —
   *  never a pre-acceptance proposal, which lives in project metadata and is not
   *  a commitment. Always an object on this payload, with null members when
   *  nothing has been captured; the CS listing does not carry the field at all
   *  and the table hides the column there. */
  budget: ProjectBudget;
  /** The delivery CEILING the client asked for, in days. Null when uncommitted. */
  deliveryMaxDays: number | null;
  /** The low end of a stated window (0208), when the client gave one. */
  deliveryMinDays?: number | null;
  /** The timeline half of the engagement structure (0156): the committed start
   *  (ISO date) and the duration shape, so an ongoing project's Timeline cell
   *  can say "Ongoing from Sep 1" instead of "-". Optional because this
   *  payload is a hand-mirrored `to_wire()` dict and an older backend simply
   *  omits them. */
  startDate?: string | null;
  durationKind?: "ongoing" | "fixed" | "trial_then_ongoing" | null;
  durationMonths?: number | null;
  durationMonthsMin?: number | null;
  /** The desk + verification tier of this project's MOST RECENTLY claimed
   *  specialist-call meeting (`meetings` table, migration 0182), or both null
   *  when never claimed. A project can hold several claims across tiers; this
   *  is the latest one, for the column badge — open `ExplorerProjectMeeting[]`
   *  on the full detail payload for the whole history. Optional because this
   *  route's payload is a hand-mirrored dict and an older backend omits them. */
  handoffTrack?: string | null;
  meetingVerification?: "unverifiable" | "pending" | "confirmed" | "canceled" | string | null;
  /** Open attention flags — the alert column. `openFlagCount` is flag ROWS;
   *  `openFlagReasons` is the DISTINCT kinds. Both come from
   *  `attention.PROJECT_FLAG_COLUMNS_SQL`, so they mean the same thing here as
   *  in the Alerts worklist. */
  openFlagCount: number;
  openFlagReasons: string[];
  /** The person in this chat is a TALENT pitching their services, not a client
   *  buying them. A durable TAG rather than an alert: it comes from the same
   *  fragment but is NOT scoped to unresolved flags, so it outlives the alert
   *  it was raised with. Optional because this route's payload is a hand-
   *  mirrored `to_wire()` dict — an older backend simply omits it. */
  sellerIntent?: boolean;
  /** Who is on this project RIGHT NOW, from `repo/project_handling.py`. All
   *  four are CURRENT and reversible, which is exactly why they are not rungs
   *  on `stage`: that ladder is cumulative, so a project whose team took the
   *  conversation over still reads "Brief" on it forever (Monday 3143611422).
   *  Optional for the same reason as `sellerIntent`, and absent altogether in
   *  support mode, whose narrower payload does not carry them. */
  humanTakeover?: boolean;
  humanTakeoverAt?: string | null;
  /** The operator's email, or the `mira-auto-stop` sentinel. See
   *  `BigClientAlert.humanTakeoverBy`. */
  humanTakeoverBy?: string | null;
  /** The explicit Pause control — the ONE thing that actually parks Mira. A
   *  takeover on its own does not (blocking was removed 2026-08-10), so these
   *  two are independent and a row can carry either, both, or neither. */
  agentPaused?: boolean;
  /** A `search_runs` row still in flight. The `search` rung only says a run
   *  EXISTS, so without this a search mid-flight and a search that finished
   *  and showed results badge identically (Monday 3138277582). */
  searchRunning?: boolean;
  /** Starred by the CALLING operator (migration 0158). The one field on this
   *  payload whose value depends on who asked. */
  starred?: boolean;
}

export interface ExplorerUserDetail {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    /** Cross-project agent notes. Admin-only; never reaches a talent. */
    agentNotes: string | null;
    blocked: boolean;
    /** The regional model tier the account was first seen on + the country it was
     *  decided from (migration 0195). Null until a request carried a country. */
    modelTier?: string | null;
    originCountry?: string | null;
    /** The client's Fiverr handle (`buyer_identities.fiverr_username`), seeded
     *  at sign-in. Null for a guest, or when the sign-in carried no username. */
    fiverrUsername?: string | null;
    /** Their Fiverr inbox conversation, built BACKEND-side so it inherits the
     *  environment's marketplace origin: a dev tester's client exists only on
     *  the dev marketplace, and a hard-coded www link 404s for them. Null
     *  whenever the handle is, so the console shows no button rather than a
     *  link into somebody else's inbox. */
    fiverrInboxUrl?: string | null;
  };
  /** The buyer identity record. Shape is owned by the backend; rendered as
   *  labelled key/value rows rather than typed field by field, because this is
   *  an audit view and an unknown new field should still show up. */
  identity: Record<string, unknown> | null;
  projects: ExplorerProjectRow[];
}

export interface ExplorerFlag {
  id: string;
  projectId: string;
  reason: string;
  detail: string | null;
  createdAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  projectName: string | null;
  ownerEmail: string | null;
  /** The client's account display name, so the worklist can name a person
   *  rather than print an email. Null when the account never carried one. */
  ownerDisplayName: string | null;
  /** The flagged project's creation stamps (migrations 0183 / 0195), the two
   *  worklist filters' keys. Null = not recorded; optional on an older backend. */
  createdDevice?: string | null;
  originCountry?: string | null;
}

/** One row of the client's brief checklist, as the CLIENT's own right rail sees
 *  it: the effective template joined with MASON's per-item verdicts. Computed
 *  server-side by the very function the search gate reads
 *  (`recruiter/agent/brief_checklist.py::compute_brief_checklist`), so the
 *  console cannot show a second opinion about whether a row is satisfied. */
export interface BriefChecklistRow {
  key: string;
  label: string;
  /** Required rows gate the client's approve control; optional ones only
   *  sharpen the match. */
  required: boolean;
  done: boolean;
  /** MASON's one-line why. Empty on a row it has not ruled on. */
  reason: string;
}

export interface BriefChecklistView {
  items: BriefChecklistRow[];
  requiredDone: number;
  requiredTotal: number;
  optionalDone: number;
  optionalTotal: number;
  /** Completion over the REQUIRED rows only — the same number the client's
   *  progress bar shows. */
  overallPct: number;
  allRequiredDone: boolean;
  /** True when Atlas tailored the list for this project; false when it is the
   *  default floor. */
  templateAuthored: boolean;
  note: string | null;
  /** The programmatic latch: set once every required row was done, and it never
   *  retracts. Can therefore disagree with `allRequiredDone` after a verdict
   *  regression, which is a thing an operator wants to see rather than have
   *  reconciled away.
   *
   *  This alone decides whether the client's approve control is on screen
   *  (`web`'s `lib/brief-ready-gate.ts`), so a disagreement is a client who can
   *  still continue while Mira works the open row. It used to be ANDed with
   *  `allRequiredDone` there, which meant the disagreement was an outage. */
  searchEnabled: boolean;
}

/** One SCOUT search this project ran, with the shortlist it produced.
 *
 *  `picks` carries the SAME `AdminSearchPick` shape the Talent console's
 *  Searches page renders — the backend runs both through one adapter, because
 *  `scout_searches.picks` is a schema-less blob and two conversions would drift.
 *
 *  This exists because `sellerThreads` alone could never answer "who did we find
 *  for this project": threads are created only under a concierge run, so a client
 *  who ran the self-serve search was never contacted by anybody and the Sellers
 *  tab came up empty even though a full shortlist had been ranked for them. */
export interface ProjectScoutSearch {
  id: string;
  /** `funnel` = the self-serve results carousel, `concierge` = the outreach
   *  flow. Open string: the vocabulary is the backend's and may grow. */
  surface: string;
  rationale: string;
  searchScore: number | null;
  /** How many candidates the gateway returned before SCOUT ranked them. */
  poolCount: number;
  rankedCount: number;
  /** SCOUT finished on a degraded path (a partial pool, a failed enrich). */
  degraded: boolean;
  /** The ATTRITION LADDER for this hunt: how many sellers the gateway returned
   *  and where the rest went, one entry per seam. Zero-valued seams are omitted
   *  by the backend, and rows written before migration 0152 carry `{}`.
   *  Keys are a bounded vocabulary (backend `scout.discovery._funnel_counts`):
   *  seen, no_gig_data, level_dropped, filter_dropped, `filter:<kind>`,
   *  unaffordable, graded, pooled, ranked. Rendered as a sentence under the
   *  count line, because `0 ranked of 0 found` alone reads as an empty
   *  marketplace whether or not that is what happened. */
  funnel: Record<string, number>;
  searchTerms: string[];
  picks: AdminSearchPick[];
  createdAt: string | null;
}

/** One specialist-call booking CLAIM (`meetings` table, migration 0182) —
 *  `recruiter/repo/meetings.py::Meeting.to_wire()`. A claim is not a
 *  confirmation: `verification` says which tier it is in.
 *
 *  - `unverifiable` — booked through a HubSpot page (`sales`/`bsm`) we hold no
 *    token for. We know the client pressed Confirm; whether a meeting exists
 *    is not observable from here, ever.
 *  - `pending` — a Calendly desk (`sourcing`) claim, awaiting reconciliation.
 *  - `confirmed` / `canceled` — only ever set from a vendor read.
 *
 *  `slotStartUtc` is null for the card's "I already booked" link, which names
 *  no time. */
export interface ExplorerProjectMeeting {
  id: string;
  projectId: string;
  track: string;
  vendor: string;
  claimSource: "slot_pick" | "already_booked_claim" | string;
  verification: "unverifiable" | "pending" | "confirmed" | "canceled" | string;
  slotStartUtc: string | null;
  durationMinutes: number | null;
  bookingUrl: string | null;
  vendorEventUri: string | null;
  claimedAt: string | null;
  confirmedAt: string | null;
  canceledAt: string | null;
}

/** The full project payload. Talent-bearing (`sellerThreads` is the real,
 *  de-anonymized outreach), so the route is gated `talent_management` OR
 *  `explorer` — never `customer_support`, which reads the narrower
 *  `/api/admin/support/conversations/{id}` instead. */
export interface ExplorerProjectDetail {
  owner: {
    id: string;
    email: string | null;
    displayName: string | null;
    /** The name a human calls this client: the account's display name, else
     *  the name they gave Mira (buyer identity). Null when neither exists. */
    clientName: string | null;
    agentNotes: string | null;
  };
  buyerId: string | null;
  channel: string | null;
  agentPaused: boolean;
  humanTakeover: boolean;
  openFlags: ExplorerFlag[];
  /** How far this project got, off the same ladder the tables badge. Open union
   *  (see `ExplorerProjectRow.stage`). */
  stage: string | null;
  /** The client's brief checklist, done/not-done per row. */
  briefChecklist: BriefChecklistView | null;
  /** Talent pitching their services, not a client buying them. Derived from the
   *  whole flag history, so it outlives the alert it was raised with. */
  sellerIntent?: boolean;
  /** The client clicked a "search Fiverr yourself" link at least once (the
   *  `search_by_myself_clicked` funnel click). Feeds the header badge; absent
   *  on a backend that predates it. */
  selfSearchClicked?: boolean;
  /** The regional model tier the project was created on + the country it was
   *  decided from (migration 0195) — the header chip. Null on older rows. */
  modelTier?: string | null;
  originCountry?: string | null;
  workspace: Record<string, unknown>;
  searchPreferences: Record<string, unknown> | null;
  buyerIdentity: Record<string, unknown> | null;
  fullMessages: ChatMessage[];
  /** Every booking claim on this project, soonest slot first (empty array on a
   *  project never routed to a desk). Absent on a backend that predates it. */
  meetings?: ExplorerProjectMeeting[];
  sellerThreads: Record<string, unknown>[];
  /** Every SCOUT search this project ran, newest first. Bounded by the backend;
   *  `searchCount` is the true total, so a truncated list can say so. */
  scoutSearches: ProjectScoutSearch[];
  searchCount: number;
  // The route also returns `fullToolCalls` (the per-turn LLM tool-call log). It is
  // deliberately NOT mirrored here: the console's Tool calls / Raw JSON tabs were
  // deleted, and the agent-debug dashboard that actually reads a trace lives in
  // `admin-internal` over `/api/admin/agent-*`. Mirroring a field nothing renders
  // is how a hand-maintained wire type rots (frontend/admin/CLAUDE.md).
}

/** What the people listing narrows and orders by, SERVER-side. Same rule as
 *  `ExplorerProjectFilters`: the list is paged, so a browser-side filter could
 *  only ever cover the pages already fetched. */
export interface ExplorerUserFilters {
  q?: string;
  /** When they JOINED — the column the console's range control is labelled with. */
  range?: DateRange;
  scope?: ExperimentScope;
  /** True = blocked accounts only, false = active only, undefined = either. */
  blocked?: boolean;
  /** True = guests only, false = signed-up only, undefined = either. */
  guests?: boolean;
  /** Regional model tiers the account was first seen on (`luna` | `terra`), plus
   *  the sentinel `unknown` for the rows carrying none. Empty = every tier. */
  tiers?: readonly string[];
  /** Origin countries the account was first seen from (ISO codes), plus the
   *  sentinel `unknown` for the rows carrying none. Empty = every country. */
  countries?: readonly string[];
  sort?: string;
  desc?: boolean;
}

export function fetchExplorerUsers(
  cursor: string | null,
  filters: ExplorerUserFilters = {},
): Promise<AdminUsersResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  appendDateRange(params, filters.range);
  appendExperimentScope(params, filters.scope);
  if (filters.blocked !== undefined) params.set("blocked", String(filters.blocked));
  if (filters.guests !== undefined) params.set("guests", String(filters.guests));
  for (const tier of filters.tiers ?? []) params.append("tiers", tier);
  for (const country of filters.countries ?? []) params.append("countries", country);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.desc !== undefined) params.set("desc", String(filters.desc));
  const qs = params.toString();
  return request<AdminUsersResponse>(`/api/admin/users${qs ? `?${qs}` : ""}`);
}

// --- TM shortlist additions (talent_management) -----------------------------
/** Sellers a talent manager added to a project's shortlist. */
export function fetchShortlist(projectId: string): Promise<ShortlistListResponse> {
  return request<ShortlistListResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/shortlist`,
  );
}

/** Add a seller (username or numeric id) to the project's shortlist. Idempotent. */
export function addShortlistSeller(
  projectId: string,
  body: ShortlistAddRequest,
): Promise<ShortlistAdditionRow> {
  return request<ShortlistAdditionRow>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/shortlist`,
    jsonBody(body),
  );
}

/** Soft-remove one addition by row id (scoped to the project). */
export function removeShortlistSeller(projectId: string, rowId: number): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/shortlist/${rowId}`,
    { method: "DELETE" },
  );
}

export function fetchExplorerUser(userId: string): Promise<ExplorerUserDetail> {
  return request<ExplorerUserDetail>(`/api/admin/users/${encodeURIComponent(userId)}`);
}

/** The store-wide filter vocabulary, sent with the FIRST page only.
 *
 *  Derived from the whole table rather than from the rows on screen: options
 *  read off a server-filtered page collapse the popover to the values already
 *  ticked, so the one control that would widen the selection stops offering it.
 *  Only the OPEN vocabularies travel — stage, work shape and handling are closed
 *  sets this bundle owns, and it lists those from its own constants. */
export interface ExplorerProjectFacets {
  kinds: string[];
  alertReasons: string[];
}

export interface ExplorerProjectsResponse {
  projects: ExplorerProjectRow[];
  /** First page only; null on every page after it. */
  facets?: ExplorerProjectFacets | null;
  /** Null on the last page. The route is keyset-paged (200 a page): it used to
   *  return every project in the system, which in prod meant a 9.4-second
   *  request against a 10-second hard timeout and a tab that had stopped
   *  loading. */
  nextCursor: string | null;
}

/** Everything the projects listing narrows and orders by, SERVER-side.
 *
 *  Server-side is the correctness condition on a paged list, not a nicety: a
 *  browser-side filter can only see the pages already fetched, so it would
 *  answer "nothing matches" from the first 200 rows while the match sat on page
 *  nine. `sort` covers the columns of `projects` itself; the table's derived
 *  columns (stage, messages, budget, alerts) are not orderable server-side and
 *  sort the loaded rows instead. */
export interface ExplorerProjectFilters {
  q?: string;
  range?: DateRange;
  scope?: ExperimentScope;
  kinds?: readonly string[];
  shapes?: readonly string[];
  /** WHO is paying (`smb` | `solo_professional` | `individual_consumer` |
   *  `enterprise` | `unclear`), plus the sentinel `unset` for projects nothing has
   *  classified yet. Server-side like every filter here: the list is paged, so a
   *  browser-side one would answer "nothing matches" from the first page while the
   *  match sat on page nine. Empty = every buyer type. */
  buyerTypes?: readonly string[];
  stages?: readonly string[];
  handling?: readonly string[];
  /** Open-flag REASONS to keep, plus the sentinel `none` for projects carrying
   *  no open flag at all — a bucket operators actively look for. Empty = either. */
  alerts?: readonly string[];
  /** Device classes the project was CREATED on, plus the sentinel `unknown` for
   *  the rows carrying none. Empty = every device. */
  devices?: readonly string[];
  /** Regional model tiers the project was CREATED on (`luna` | `terra`), plus the
   *  sentinel `unknown` for the rows carrying none. Empty = every tier. */
  tiers?: readonly string[];
  /** Origin countries the project was CREATED from (ISO codes), plus the sentinel
   *  `unknown` for the rows carrying none. Empty = every country. */
  countries?: readonly string[];
  /** Verification tiers of the project's specialist-call booking claims
   *  (`unverifiable` | `pending` | `confirmed` | `canceled`), plus the sentinel
   *  `none` for a project that never claimed one. EXISTS server-side, not an
   *  equality: a project can hold several claims. Empty = every project. */
  meetings?: readonly string[];
  /** True = only the CALLER's own starred projects. */
  starred?: boolean;
  sort?: string;
  desc?: boolean;
}

export function fetchExplorerProjects(
  cursor: string | null,
  filters: ExplorerProjectFilters = {},
): Promise<ExplorerProjectsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (filters.q?.trim()) params.set("q", filters.q.trim());
  appendDateRange(params, filters.range);
  appendExperimentScope(params, filters.scope);
  for (const kind of filters.kinds ?? []) params.append("kinds", kind);
  for (const shape of filters.shapes ?? []) params.append("shapes", shape);
  for (const type of filters.buyerTypes ?? []) params.append("buyer_types", type);
  for (const stage of filters.stages ?? []) params.append("stages", stage);
  for (const state of filters.handling ?? []) params.append("handling", state);
  for (const reason of filters.alerts ?? []) params.append("alerts", reason);
  for (const device of filters.devices ?? []) params.append("devices", device);
  for (const tier of filters.tiers ?? []) params.append("tiers", tier);
  for (const country of filters.countries ?? []) params.append("countries", country);
  for (const meeting of filters.meetings ?? []) params.append("meetings", meeting);
  if (filters.starred) params.set("starred", "true");
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.desc !== undefined) params.set("desc", String(filters.desc));
  const qs = params.toString();
  return request<ExplorerProjectsResponse>(`/api/admin/projects${qs ? `?${qs}` : ""}`);
}

export function fetchExplorerProject(projectId: string): Promise<ExplorerProjectDetail> {
  return request<ExplorerProjectDetail>(`/api/admin/projects/${encodeURIComponent(projectId)}`);
}

/** Ask for an English reading of this project's client conversation.
 *
 *  Returns the moment the work is queued, not when it is done: a model call
 *  can't run on the request cycle, so the translation arrives as a
 *  `conversation.translation.ready` pointer on the returned channel and the
 *  payload is fetched with `fetchConversationTranslation`. Same gate as reading
 *  the project. */
export function postConversationTranslate(projectId: string): Promise<ConversationTranslateAck> {
  return request<ConversationTranslateAck>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/translate`,
    jsonBody({}),
  );
}

/** One finished translation, by the key its ready event carried. A 404 means the
 *  cached translation aged out and the conversation has to be translated again. */
export function fetchConversationTranslation(
  projectId: string,
  key: string,
): Promise<ConversationTranslation> {
  return request<ConversationTranslation>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/translation/${encodeURIComponent(key)}`,
  );
}

/** Star or un-star a project for the CALLING operator (migration 0158).
 *
 *  Plain employee scope, not an operator write: the star reaches no client and
 *  changes no automation, so it is not gated behind `full` — a read-only
 *  reviewer follows projects too. */
export function setProjectStar(
  projectId: string,
  starred: boolean,
): Promise<{ projectId: string; starred: boolean }> {
  return request<{ projectId: string; starred: boolean }>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/star`,
    { ...jsonBody({ starred }), method: "PUT" },
  );
}

/** One of a project's uploaded files, as bytes.
 *
 *  Fetched as a Blob rather than linked to with `<a href download>`: the admin
 *  identity rides a request HEADER (stub mode) plus a cross-origin cookie, and a
 *  plain top-level navigation carries neither, so the link would 401. The caller
 *  turns the Blob into a save.
 *
 *  Gated `talent_management` OR `explorer`, the same union as the detail the file
 *  is listed from. A support-mode viewer calls `fetchSupportProjectFile`. */
export function fetchExplorerProjectFile(projectId: string, fileId: string): Promise<Blob> {
  return blobRequest(
    `/api/admin/projects/${encodeURIComponent(projectId)}/files/${encodeURIComponent(fileId)}`,
  );
}

/** Ask for a project's portable blueprint: the brief (sections/blocks) plus the whole
 *  agent trace, as JSON. Available in EVERY env.
 *
 *  Assembling it rehydrates one storage object per captured agent step, which cannot
 *  happen on the request cycle (doctrine 1.1). The synchronous GET this replaced took
 *  8.9s on average against a 10s server timeout and returned 504 in bursts, so this
 *  POSTs and returns 202 + a `turn_id`; the worker writes the bundle and publishes
 *  `admin.export.ready` on the operator's own Pusher channel with the download URL.
 *
 *  Identical wire shape to `requestProjectBriefExport`, including the already-exists
 *  fast path: `url`/`filename` come back populated when an earlier click already
 *  produced the bytes, and then no event is coming.
 *  `backend/web/routes/admin_project_io.py`. */
export function requestProjectBlueprintExport(projectId: string): Promise<AdminExportResponse> {
  return request<AdminExportResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/export`,
    { method: "POST" },
  );
}

/** Ask for the project's brief as the DOCUMENT a client or talent actually receives.
 *
 *  Rendering it means driving a headless browser, which cannot happen on the request
 *  cycle (doctrine 1.1 — the synchronous `brief.pdf` this replaced took ~9.7s against
 *  a 10s server timeout and failed about half the time). So this POSTs and returns
 *  202 + a `turn_id`; the worker renders and publishes `admin.export.ready` on the
 *  operator's own Pusher channel with the download URL.
 *
 *  `url`/`filename` come back populated when the bytes ALREADY exist — a repeat click
 *  that collapsed onto a finished render — and then no event is coming, so download
 *  straight from the response. `backend/web/routes/admin.py`. */
export function requestProjectBriefExport(projectId: string): Promise<AdminExportResponse> {
  return request<AdminExportResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/brief.export`,
    { method: "POST" },
  );
}

/** The rendered PDF's bytes, by export id. Owner-scoped server-side: an operator
 *  reads only the exports they asked for.
 *
 *  A blob rather than `<a href download>` for the same reason as every download here
 *  (`fetchExplorerProjectFile`) — and it is why this route streams instead of
 *  redirecting to a signed bucket URL the way the buyer and talent twins do. */
export function fetchAdminExport(url: string): Promise<Blob> {
  return blobRequest(url);
}

/** The same document as HTML, the on-cycle render that needs no browser.
 *
 *  The degrade path when the PDF cannot be produced (no renderer on a local stack, a
 *  soft-failed render): rather than hand an operator a dead button, they still walk
 *  away with the brief, the same order the client's own download takes
 *  (`web/src/lib/brief-download.ts`). Carries the identical employee gate, so a
 *  401/403/404 fails here too and is re-thrown for the caller to surface
 *  (doctrine 1.5) rather than degrading silently. */
export function fetchProjectBriefHtml(projectId: string): Promise<Blob> {
  return blobRequest(`/api/admin/projects/${encodeURIComponent(projectId)}/brief.html`);
}

/** Import a previously-exported blueprint as a NEW project in THIS env. DEV-ONLY server
 *  side (a 404 in preprod/prod); the caller also hides the control outside dev. */
export function importProject(payload: ProjectExport): Promise<ImportProjectResponse> {
  return request<ImportProjectResponse>("/api/admin/projects/import", jsonBody(payload));
}

/** One talent, across every project they were approached for.
 *
 *  Talent-bearing, so gated `talent_management` OR `explorer` — the same union as
 *  the project detail, because it is the same `seller_threads` rows keyed by
 *  talent instead of by project. A `customer_support`-only caller gets 403.
 *
 *  This is the OPERATIONAL half of the talent page only. The statistics half is
 *  BI-owned and comes from `fetchTalentMetrics`; there is deliberately no rate or
 *  average in this payload (frontend/admin/ARCHITECTURE.md). */
export interface ExplorerTalentDetail {
  /** Also the talent's Fiverr username. */
  sellerId: string;
  profileUrl: string | null;
  /** SCOUT's compact profile from the freshest thread that has one. Shape is the
   *  gateway's, so it is parsed defensively rather than typed field by field
   *  (lib/seller-threads.ts). */
  profile: Record<string, unknown>;
  /** Newest project first. Each carries its project + owner and the full thread. */
  threads: Record<string, unknown>[];
}

export function fetchExplorerTalent(sellerId: string): Promise<ExplorerTalentDetail> {
  return request<ExplorerTalentDetail>(`/api/admin/talents/${encodeURIComponent(sellerId)}`);
}

// --- Alerts: the raised-flag worklist ----------------------------------------

export interface AlertsResponse {
  flags: ExplorerFlag[];
  /** Null on the last page. */
  nextCursor: string | null;
  /** The distinct OPEN reasons across the whole store (not this page), so the
   *  filter control offers the full vocabulary while a selection or a search
   *  has narrowed the rows. */
  reasons: string[];
}

/** The Alerts worklist's server-side narrowing: the reason multi-select and
 *  the free-text search both run in SQL across the WHOLE store, because the
 *  list is keyset-paged and a browser-side filter could only ever cover the
 *  pages already fetched. */
export interface AlertsFilter {
  reasons?: readonly string[];
  /** The flagged project's creation stamps (`created_device` / `origin_country`),
   *  each with the sentinel `unknown` for a project carrying none. Empty = all. */
  devices?: readonly string[];
  countries?: readonly string[];
  q?: string;
  /** The cohort range, applied in SQL like every other filter here so it
   *  narrows the WHOLE lane rather than the pages already fetched. */
  range?: DateRange;
}

/** One keyset page of OPEN attention flags, newest first. Requires the `alerts`
 *  area.
 *
 *  Paged rather than capped: this list used to stop at the newest 100 and tell
 *  the operator to resolve alerts to reach older ones, which is asking them to
 *  act on rows they have not read. */
export function fetchAlerts(
  cursor: string | null,
  filter: AlertsFilter = {},
): Promise<AlertsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  for (const reason of filter.reasons ?? []) params.append("reasons", reason);
  for (const device of filter.devices ?? []) params.append("devices", device);
  for (const country of filter.countries ?? []) params.append("countries", country);
  if (filter.q) params.set("q", filter.q);
  appendDateRange(params, filter.range);
  const qs = params.toString();
  return request<AlertsResponse>(`/api/admin/alerts${qs ? `?${qs}` : ""}`);
}

/** Mark one alert handled. Idempotent server-side: only an OPEN row matches, so
 *  a double-click resolves once and then 404s rather than double-writing. */
export function resolveAlert(flagId: string): Promise<{ flagId: string; resolved: boolean }> {
  return request(`/api/admin/alerts/${encodeURIComponent(flagId)}/resolve`, { method: "POST" });
}

// --- Big clients: the big-fish desk ------------------------------------------
//
// The list is the SAME `attention_flags` store the Alerts worklist above reads,
// narrowed to `reason = 'big_client'` and enriched with the two facts the desk
// acts on: the committed budget, and whether a human has already taken the
// project over (`recruiter/repo/attention.list_big_client_alerts`).
//
// These are the console's FIRST write routes that change what a client sees, so
// each one is named for what the client experiences, not for the column it sets.
// All are plain employee scope on the backend, NOT area-gated: `require_area` is
// not what holds them, the confirm dialogs and the audit trail are.

export interface BigClientAlert {
  /** The attention-flag id. Dismissing the alert resolves THIS, and deliberately
   *  leaves the takeover state alone. */
  id: string;
  projectId: string;
  reason: string;
  detail: string | null;
  createdAt: string | null;
  projectName: string | null;
  ownerEmail: string | null;
  ownerDisplayName: string | null;
  /** Committed budget in cents (`search_preferences`). Either end may be null:
   *  a lone ceiling is the common shape, and both null means the flag fired on a
   *  figure that has since been cleared. */
  budgetMinCents: number | null;
  budgetMaxCents: number | null;
  /** The committed RATE (engagement structure, 0156) — the flag can fire on
   *  one month of it, so the desk row must be able to show it. Optional
   *  because an older backend omits the fields (hand-mirrored payload). */
  budgetHourlyCents?: number | null;
  rateAmountCents?: number | null;
  rateUnit?: "hour" | "day" | "week" | "month" | "year" | "deliverable" | null;
  rateDeliverableNoun?: string | null;
  ratePerPerson?: boolean;
  cadenceCount?: number | null;
  cadenceUnit?: "week" | "month" | null;
  /** The low ends of a stated rate band / workload band (0208). */
  rateAmountMinCents?: number | null;
  cadenceCountMin?: number | null;
  /** The engagement SHAPE the agents latched ("project" / "ongoing" /
   *  "hybrid") — a big client asking for ongoing work is a different
   *  conversation than a one-off. Null = not classified yet; optional because
   *  an older backend omits it. */
  workShape?: string | null;
  /** The project's creation stamps (migrations 0183 / 0195), the desk's two
   *  cohort filters' keys. Null = not recorded; optional on an older backend. */
  createdDevice?: string | null;
  originCountry?: string | null;
  /** True once Stop (or a Pause on a big client) stamped `human_takeover_at`. */
  humanTakeover: boolean;
  /** WHO took them over — the operator's email, or the `mira-auto-stop`
   *  sentinel when the automatic big-budget engage fired and no human pressed
   *  anything. Null when `humanTakeover` is false, and also on an older backend
   *  that carried the boolean alone, which is why it is optional. Rendered
   *  verbatim rather than prettified: relabelling an automatic action as a
   *  person's is worse than showing a raw sentinel. */
  humanTakeoverBy?: string | null;
  /** Which desk this handoff was routed to ("sales" / "bsm" / "sourcing"), i.e.
   *  WHOSE calendar the client was shown. Null when the project qualified for no
   *  desk, which is the ordinary case: routing is gated on the same committed-
   *  budget tier as the automatic handoff. Optional because an older backend
   *  omits it. Narrow it through `handoffDeskLabel` rather than printing it: the
   *  value comes off a free-form metadata bag. */
  handoffTrack?: string | null;
  /** Whether the client says they booked that meeting. A CLAIM, not proof: their
   *  browser reports the embedded scheduler's confirmation, and an "I already
   *  booked" link covers the bookings it cannot see. Meaningless without
   *  `handoffTrack` (no desk means no meeting was ever offered), which is why the
   *  two are read together through `meetingBookingState`. */
  handoffMeetingBooked?: boolean;
}

export interface BigClientsResponse {
  alerts: BigClientAlert[];
  /** Null on the last page. */
  nextCursor: string | null;
}

/** The Big-clients desk's server-side narrowing: the human-takeover state, the
 *  project's work shape (`project` | `ongoing` | `hybrid` | `none`), and the
 *  free-text search, all applied in SQL across the whole desk. Both states (or
 *  neither) selected filters nothing. */
export interface BigClientsFilter {
  states?: readonly string[];
  shapes?: readonly string[];
  /** Which desk the handoff was routed to (`sales` | `bsm` | `sourcing`, plus
   *  `none` for routed nowhere). */
  desks?: readonly string[];
  /** Whether that desk's calendar was booked (`booked` | `not_booked` | `none`
   *  for a project that was never offered a meeting). */
  meetings?: readonly string[];
  /** The project's creation stamps (`created_device` / `origin_country`), each
   *  with the sentinel `unknown` for a project carrying none. Empty = all. */
  devices?: readonly string[];
  countries?: readonly string[];
  q?: string;
  /** The cohort range, applied in SQL like every other filter here so it
   *  narrows the WHOLE lane rather than the pages already fetched. */
  range?: DateRange;
}

/** One keyset page of OPEN big-client alerts, newest first. Same cursor
 *  contract as the worklist above; it is the same table, narrowed to one
 *  reason. */
export function fetchBigClients(
  cursor: string | null,
  filter: BigClientsFilter = {},
): Promise<BigClientsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  for (const state of filter.states ?? []) params.append("states", state);
  for (const shape of filter.shapes ?? []) params.append("shapes", shape);
  for (const desk of filter.desks ?? []) params.append("desks", desk);
  for (const meeting of filter.meetings ?? []) params.append("meetings", meeting);
  for (const device of filter.devices ?? []) params.append("devices", device);
  for (const country of filter.countries ?? []) params.append("countries", country);
  if (filter.q) params.set("q", filter.q);
  appendDateRange(params, filter.range);
  const qs = params.toString();
  return request<BigClientsResponse>(`/api/admin/big-clients${qs ? `?${qs}` : ""}`);
}

/** **Stop** (`active`) / **Resume**: freeze or release ALL automated advancement
 *  for a project and show or clear the client's "a human from our team will
 *  contact you in your Fiverr inbox" popup.
 *
 *  Stronger than `postAgentControl`, which only parks agent turns. Idempotent:
 *  the repo stamp is the one-shot key, so a double click never re-pops the client
 *  or duplicates the audit trail. Stopping also snapshots the CS handoff. */
export function postTakeover(
  projectId: string,
  active: boolean,
): Promise<{ projectId: string; humanTakeover: boolean }> {
  const body: TakeoverRequest = { active };
  return request(`/api/admin/projects/${encodeURIComponent(projectId)}/takeover`, jsonBody(body));
}

/** Pause / resume the agent for one project. While paused the worker persists the
 *  client's messages but never runs a turn, so the operator answers instead.
 *
 *  On a big client the backend treats a pause as the same client-visible handoff
 *  as Stop (it stamps the takeover and pops the contact notice), which is why the
 *  button on this desk says so. */
export function postAgentControl(
  projectId: string,
  paused: boolean,
): Promise<{ projectId: string; agentPaused: boolean }> {
  const body: AgentControlRequest = { paused };
  return request(
    `/api/admin/projects/${encodeURIComponent(projectId)}/agent-control`,
    jsonBody(body),
  );
}

/** The two flows a client can be put into, the same pair their own approve
 *  control offers them: `concierge` = Mira reaches out to talent herself,
 *  `search` = the plain funnel runs and they land on a browsable shortlist. */
export type ApproveBriefLane = "concierge" | "search";

export interface ApproveBriefResult {
  projectId: string;
  /** The flow that actually ran. Echoed back rather than assumed, so the UI
   *  reports what the server did instead of what it asked for. */
  lane: ApproveBriefLane;
  /** The run the client is now in. When `started` is false this is the run that
   *  was ALREADY going, not a new one. */
  runId: string;
  /** Whether this call is what began the run. False = a run was already active
   *  and the server adopted it instead of starting a second one. */
  started: boolean;
  searchEnabled: boolean;
}

/** **Approve brief**: open the search gate for the client and start the flow the
 *  operator picked, the two halves of the approve control inside their own brief.
 *
 *  Overrides an unfinished brief checklist on purpose, which is the point: while
 *  a required row is open the client's own control never appears, so a client
 *  Mira cannot unblock has no other way forward. Then it starts the same run
 *  that lane's own client route starts, so there is no operator-only variant of
 *  a run for anyone to reason about later.
 *
 *  `lane` is what makes this an answer to the question the client's own control
 *  would have asked THEM. It is always sent explicitly: the server defaults a
 *  body-less call to `concierge` for the old build mid rolling deploy, and that
 *  default is a compatibility floor, not something a live caller should lean on.
 *
 *  Idempotent per lane: a second press adopts that lane's active run rather than
 *  starting a second one (`started: false`). Refused with 409 while a human has
 *  the project taken over, and on a top-tier committed budget the server runs the
 *  big-client Stop as well, exactly as the client's own click would. */
export function postApproveBrief(
  projectId: string,
  lane: ApproveBriefLane,
): Promise<ApproveBriefResult> {
  return request(
    `/api/admin/projects/${encodeURIComponent(projectId)}/approve-brief`,
    jsonBody({ lane }),
  );
}

/** Send a human reply to the client **as Mira**. The client sees an ordinary
 *  assistant message (the takeover is deliberately seamless); only the admin
 *  thread badges it, from `metadata.operator`. Also resolves the
 *  waiting/human-requested/frustration/misuse flags, since a human just answered. */
export function postOperatorMessage(
  projectId: string,
  text: string,
): Promise<{ projectId: string; message: ChatMessage }> {
  const body: OperatorMessageRequest = { text };
  return request(
    `/api/admin/projects/${encodeURIComponent(projectId)}/operator-message`,
    jsonBody(body),
  );
}

/** Shared reply snippets for the operator composer (any employee's, not per-user). */
export interface SavedReply {
  id: string;
  title: string;
  body: string;
}

export function fetchSavedReplies(): Promise<{ replies: SavedReply[] }> {
  return request<{ replies: SavedReply[] }>("/api/admin/saved-replies");
}

export function createSavedReply(title: string, body: string): Promise<{ reply: SavedReply }> {
  const payload: SavedReplyCreateRequest = { title, body };
  return request<{ reply: SavedReply }>("/api/admin/saved-replies", jsonBody(payload));
}

export function deleteSavedReply(replyId: string): Promise<{ deleted: string }> {
  return request(`/api/admin/saved-replies/${encodeURIComponent(replyId)}`, { method: "DELETE" });
}

// --- Developers: the agent debug dashboard -----------------------------------
//
// Reads over `agent_trace` — the tee of every LLM call every agent made, with the
// exact rendered prompt it received and the text and tool calls it produced. Every
// shape here is GENERATED from the backend Pydantic models (`web/routes/debug.py`),
// unlike the legacy `to_wire()` reads above, so nothing on this surface is
// hand-mirrored and there is nothing to drift.
//
// The routes are plain employee scope on the backend, because admin-internal's
// debug dashboard reads the same endpoints and a Fiverr-domain employee in dev
// holds no persisted grant. The `developers` area gates the PAGE; narrowing the
// routes waits for admin-internal's deletion.

/** Turns-list query. Search, the project scope and the cursor are all SERVER-side,
 *  so a filter or a scroll spans every turn rather than the page already loaded. */
export interface DebugTurnsQuery {
  projectId?: string;
  /** The synthetic "No project" bucket — turns with no `project_id` at all. */
  noProject?: boolean;
  q?: string;
  before?: string;
  limit?: number;
  /** The cohort range over the turn's `ended_at`, applied in SQL so it spans
   *  every turn rather than the page already loaded. */
  range?: DateRange;
  /** Keep only turns in which at least one of these agents fired. OR within the
   *  set, like every other multi-select in this console. */
  agents?: readonly string[];
}

/** Build the `/debug/turns` path with its search/cursor/limit query string. Pure
 *  (no fetch) so it is unit-tested directly. A blank `q` is DROPPED: an empty
 *  search box must read as no filter, not as a filter matching the empty string.
 *  An empty `agents` is dropped for the same reason. */
export function buildDebugTurnsPath(params: DebugTurnsQuery = {}): string {
  const sp = new URLSearchParams();
  if (params.projectId) sp.set("project_id", params.projectId);
  if (params.noProject) sp.set("no_project", "true");
  if (params.q && params.q.trim()) sp.set("q", params.q.trim());
  if (params.before) sp.set("before", params.before);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.agents && params.agents.length > 0) sp.set("agents", params.agents.join(","));
  appendDateRange(sp, params.range);
  const qs = sp.toString();
  return `/api/admin/debug/turns${qs ? `?${qs}` : ""}`;
}

/** Build the `/debug/projects` path with optional server-side search + limit.
 *  Pure; same blank-`q` rule as above. */
export function buildDebugProjectsPath(params: { q?: string; limit?: number } = {}): string {
  const sp = new URLSearchParams();
  if (params.q && params.q.trim()) sp.set("q", params.q.trim());
  if (params.limit != null) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  return `/api/admin/debug/projects${qs ? `?${qs}` : ""}`;
}

/** Build the per-agent global feed path, optionally with the `before` cursor (the
 *  previous page's oldest `started_at`). Pure. */
export function buildAgentRunsPath(agent: string, before?: string | null): string {
  const q = before ? `?before=${encodeURIComponent(before)}` : "";
  return `/api/admin/debug/agent/${encodeURIComponent(agent)}${q}`;
}

/** Projects that have agent activity, most recently active first. */
export function fetchDebugProjects(
  params: { q?: string; limit?: number } = {},
): Promise<DebugProjectsResponse> {
  return request<DebugProjectsResponse>(buildDebugProjectsPath(params));
}

/** One project's turns, newest first, keyset-paged by `ended_at`. */
export function fetchDebugTurns(params: DebugTurnsQuery = {}): Promise<DebugTurnsResponse> {
  return request<DebugTurnsResponse>(buildDebugTurnsPath(params));
}

/** Every agent's run within ONE turn — what fills the agent tabs. */
export function fetchTurnRuns(turnId: string): Promise<TurnRunsResponse> {
  return request<TurnRunsResponse>(`/api/admin/debug/turn/${encodeURIComponent(turnId)}`);
}

/** One agent's runs across every turn, newest first, keyset-paged by `started_at`. */
export function fetchAgentRuns(agent: string, before?: string | null): Promise<AgentRunsResponse> {
  return request<AgentRunsResponse>(buildAgentRunsPath(agent, before));
}

/** Ask why an agent did what it did on a turn. ENQUEUES only (the answer is slow
 *  LLM work, doctrine 1.1): the ack carries the question id, and the reply arrives
 *  on the employee's own Pusher channel. */
export function postDebugChat(body: DebugChatRequest): Promise<DebugChatAck> {
  return request<DebugChatAck>("/api/admin/debug/chat", jsonBody(body));
}

// --- Launch day dashboard ---------------------------------------------------

/** The rollout TV snapshot. `token` is the remote-view credential the kiosk
 *  holds instead of an employee identity (see lib/launch.ts); an employee
 *  session omits it and rides the ordinary auth. */
/** The Funnel section's aggregate snapshot for one cohort window. Requires the
 *  `funnel` area; the payload is counts and averages only, never a record. */
export function fetchAdminFunnel(window: FunnelPresetWindow): Promise<AdminFunnelResponse> {
  const params = new URLSearchParams({ window });
  return request<AdminFunnelResponse>(`/api/admin/funnel?${params}`);
}

/** Write `?experiment=&arm=` for a group-filtered read, or nothing.
 *
 *  BOTH halves or neither, the same rule `parseScope` enforces on the way in: an
 *  arm without an experiment names nothing, and sending half of a scope would
 *  make a page look filtered by something the URL never asked for. */
function appendExperimentScope(params: URLSearchParams, scope?: ExperimentScope): void {
  if (!scope) return;
  params.set("experiment", scope.experiment);
  params.set("arm", scope.arm);
}

/** The same funnel over a CUSTOM cohort range, computed on demand.
 *
 *  A separate route from the preset read above because it is a different kind of
 *  answer: the four presets are materialized together by a worker tick and read
 *  from cache, while a range nobody could precompute is computed when asked for.
 *  So this one can legitimately come back `computing` with no funnel, and the
 *  caller polls until it is `ready` (`funnelRangeQuery` owns that bound). */
export function fetchAdminFunnelRange(
  range: DateRange,
  scope?: ExperimentScope,
  devices: readonly string[] = [],
  countries: readonly string[] = [],
  budget: BudgetRange = EMPTY_BUDGET_RANGE,
): Promise<AdminFunnelRangeResponse> {
  const params = new URLSearchParams();
  appendDateRange(params, range);
  appendExperimentScope(params, scope);
  // The device the projects were CREATED on. Sent sorted so two spellings of the
  // same selection cannot become two cache keys and two computes on the server.
  for (const device of [...devices].sort()) params.append("devices", device);
  // The origin country the projects were created from, sorted for the same reason.
  for (const country of [...countries].sort()) params.append("countries", country);
  // The committed-budget bound, USD, either side open. Only a side that is set
  // is sent: both absent is the unbounded cohort the presets are cut from.
  if (budget.min !== undefined) params.set("budget_min", String(budget.min));
  if (budget.max !== undefined) params.set("budget_max", String(budget.max));
  const qs = params.toString();
  return request<AdminFunnelRangeResponse>(`/api/admin/funnel/range${qs ? `?${qs}` : ""}`);
}

/** Every registered A/B experiment with its live allocation dial. Requires
 *  `system_admin` at read; the System Admin page is its only surface. */
/** Every registered prompt with its slots, live dials and live allocations.
 *  `system_admin` at READ: where a dial sits is a fact about the platform. */
export function fetchPromptSections(): Promise<AdminPromptSectionsResponse> {
  return request<AdminPromptSectionsResponse>("/api/admin/prompt-sections");
}

/** The full text of ONE section, fetched only when a reader opens it. The tree
 *  is half a million characters; shipping all of it with the listing would be
 *  slow for everyone in order to serve the few who look. */
export async function fetchPromptSectionText(key: string) {
  return request<AdminPromptText>(`/api/admin/prompt-sections/${encodeURIComponent(key)}/text`);
}

/** Change what share of people have this section at all. `system_admin` at FULL:
 *  this changes what a live client's prompt says. `reason` is mandatory — it is
 *  the only field that still makes sense to a reader in three months. */
export function setPromptSectionDial(key: string, pct: number, reason: string): Promise<void> {
  return request<void>(
    `/api/admin/prompt-sections/${encodeURIComponent(key)}/dial`,
    jsonBody({ pct, reason }),
  );
}

/** Reallocate one slot's approaches. One request per SLOT, not per approach: a
 *  reallocation is one decision, and two writes would let a reader see a state
 *  that never existed. */
export function setPromptApproachWeights(
  key: string,
  weights: Record<string, number>,
  reason: string,
): Promise<void> {
  return request<void>(
    `/api/admin/prompt-sections/${encodeURIComponent(key)}/approaches`,
    jsonBody({ weights, reason }),
  );
}

/** What ONE person would get for this slot, right now, through the same
 *  `choose()` the turn runs. The guard against a console that says 40% over a
 *  population sitting entirely on the control. */
export function previewPromptSection(key: string, userId: string): Promise<AdminPromptPreview> {
  return request<AdminPromptPreview>(
    `/api/admin/prompt-sections/${encodeURIComponent(key)}/preview?user_id=${encodeURIComponent(userId)}`,
  );
}

/** The three dials that govern ONE injection: how many projects are enrolled,
 *  and whether the text is sent when the trigger fired / did not fire.
 *  `system_admin` at READ. */
export function fetchInjectionDials(key: string): Promise<AdminInjectionDials> {
  return request<AdminInjectionDials>(
    `/api/admin/prompt-sections/${encodeURIComponent(key)}/injection-dials`,
  );
}

/** Set all three injection dials as ONE decision with one reason. `system_admin`
 *  at FULL: this changes what a live client's prompt says. Whole percentages. */
export function setInjectionDials(key: string, body: SetInjectionDialsRequest): Promise<void> {
  return request<void>(
    `/api/admin/prompt-sections/${encodeURIComponent(key)}/injection-dials`,
    jsonBody(body),
  );
}

/** The worker-built experimentation snapshot: every experiment with its arm
 *  strips, every injection's 2x2 cells per window, last tick's guardrail
 *  breaches. `state` is `computing` until the first tick lands. `system_admin`
 *  at READ. */
export function fetchExperimentation(): Promise<AdminExperimentationResponse> {
  return request<AdminExperimentationResponse>("/api/admin/experimentation");
}

export interface LifecycleFilters {
  entityKind?: string;
  entityKey?: string;
  limit?: number;
}

/** The lifecycle ledger, newest first. `system_admin` at READ. */
export function fetchExperimentationLifecycle(
  filters: LifecycleFilters = {},
): Promise<AdminLifecycleResponse> {
  const params = new URLSearchParams();
  if (filters.entityKind) params.set("entity_kind", filters.entityKind);
  if (filters.entityKey) params.set("entity_key", filters.entityKey);
  if (filters.limit !== undefined) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return request<AdminLifecycleResponse>(
    `/api/admin/experimentation/lifecycle${qs ? `?${qs}` : ""}`,
  );
}

/** What ONE project was on: its A/B exposures, recorded approach picks, and
 *  every injection decision. `system_admin` at READ. */
export function fetchProjectExperiments(
  projectId: string,
): Promise<AdminProjectExperimentsResponse> {
  return request<AdminProjectExperimentsResponse>(
    `/api/admin/experimentation/projects/${encodeURIComponent(projectId)}`,
  );
}

export function fetchAdminExperiments(): Promise<AdminExperimentsResponse> {
  return request<AdminExperimentsResponse>("/api/admin/experiments");
}

/** Move one experiment's allocation slider. `system_admin` at FULL: this moves
 *  live traffic between arms. Returns the refreshed dial list.
 *
 *  RESTORED 2026-09-11 alongside the System Admin card that calls it (#1231
 *  deleted both when /experiments took over). The route it posts to never went
 *  anywhere, and `/experiments` writes through the newer per-kind allocation
 *  endpoint, so the two controls are separate paths onto the same dial. See
 *  `experiment-dials.tsx` for why the old card is back and what retires it.
 */
export function setExperimentDial(
  name: string,
  pct: number,
  reason: string,
): Promise<AdminExperimentsResponse> {
  return request<AdminExperimentsResponse>(
    `/api/admin/experiments/${encodeURIComponent(name)}/dial`,
    jsonBody({ pct, reason }),
  );
}

/** One experiment's live allocation, whatever its kind, with its audit half
 *  (who set it, when, why; null while it sits on the code default).
 *  `system_admin` at READ. */
export function fetchAllocation(key: string, kind = ""): Promise<AdminAllocationResponse> {
  // `kind` disambiguates a declared slot, which is TWO experiments under one
  // key: its dial (`section`) and its approach weights (`prompt`). Omitted, the
  // route picks the key's one kind, or `prompt` for a slot that has both.
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  return request<AdminAllocationResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/allocation${q}`,
  );
}

/** Move one experiment's allocation as ONE decision with one reason, whatever
 *  its kind: `{arm: pct}` for a product experiment, `{approach: pct}` for a
 *  prompt slot, `{exposure, p_triggered, p_untriggered}` for an injection. The
 *  route validates the payload per kind. `system_admin` at FULL: this moves
 *  live traffic. Returns the refreshed allocation. */
export function setAllocation(
  key: string,
  body: SetAllocationRequest,
): Promise<AdminAllocationResponse> {
  return request<AdminAllocationResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/allocation`,
    jsonBody(body),
  );
}

/** This experiment's guardrail overrides beside the house defaults they modify.
 *  `system_admin` at READ. */
/** Everything the inspector draws for ONE experiment, whatever its kind: the
 *  facts, the thing it is about, and every arm with what distinguishes it.
 *
 *  `kind` disambiguates the one case where a key is not unique - a dialled
 *  section that also declares approaches is two experiments under one name.
 *  `system_admin` at READ. */
export function fetchExperimentDetail(key: string, kind?: string): Promise<AdminExperimentDetail> {
  const query = kind ? `?kind=${encodeURIComponent(kind)}` : "";
  return request<AdminExperimentDetail>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/detail${query}`,
  );
}

export function fetchGuardrailThresholds(key: string): Promise<AdminGuardrailThresholdsResponse> {
  return request<AdminGuardrailThresholdsResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/guardrails`,
  );
}

/** Override this experiment's guardrail thresholds. A field left out inherits
 *  the house default rather than being pinned to zero. `system_admin` at FULL. */
export function setGuardrailThresholds(
  key: string,
  body: SetGuardrailThresholdsRequest,
): Promise<AdminGuardrailThresholdsResponse> {
  return request<AdminGuardrailThresholdsResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/guardrails`,
    jsonBody(body),
  );
}

/** Record the verdict: promote or drop, which arm won, and why. The READOUT is
 *  snapshotted server-side, never sent from here, so a decision cannot be filed
 *  against numbers the platform cannot reproduce. Deciding does not move the
 *  dial. `system_admin` at FULL. */
export function decideExperiment(
  key: string,
  body: DecideExperimentRequest,
): Promise<AdminExperimentDecision> {
  return request<AdminExperimentDecision>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/decide`,
    jsonBody(body),
  );
}

/** Stop this experiment: every non-baseline arm to zero, one row, one reason.
 *  A kill is a dial going down, so the canary rule never refuses it.
 *  `system_admin` at FULL. */
export function killExperiment(
  key: string,
  body: KillExperimentRequest,
): Promise<AdminAllocationResponse> {
  return request<AdminAllocationResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/kill`,
    jsonBody(body),
  );
}

/** Put EVERY experiment back to the allocation its declaration carries.
 *
 *  One request for the whole sweep, and one transaction server side, which is the
 *  deliberate opposite of the bulk kill above: a kill is a stop-loss and should
 *  keep whatever ground it gains, while a half-restored console is the confusing
 *  state this operation exists to escape.
 *
 *  Pass `dryRun` to ask what WOULD move without writing. The confirm step must use
 *  it rather than reading the register's table, which is a worker snapshot up to
 *  half an hour old and would name the wrong experiments. `system_admin` at FULL. */
export function restoreExperimentDefaults(
  body: RestoreDefaultsRequest,
): Promise<RestoreDefaultsResponse> {
  return request<RestoreDefaultsResponse>(
    "/api/admin/experimentation/restore-defaults",
    jsonBody(body),
  );
}

/** Per arm: the error classes on `agent_trace` and the negative tag findings,
 *  read off the snapshot the A/B tick built (#1260). The window is the tick's,
 *  reported back as `windowHours`, so there is no window to ask for. `partial`
 *  is true when no snapshot has been written yet and the lists are therefore
 *  incomplete. `system_admin` at READ. */
export function fetchExperimentIssues(key: string): Promise<AdminExperimentIssuesResponse> {
  return request<AdminExperimentIssuesResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/issues`,
  );
}

/** Every project in one arm of one experiment, for ANY kind. This is what makes
 *  the explorer's experiment filter work past the two product cohorts.
 *  `system_admin` at READ. */
export function fetchArmProjects(key: string, arm: string): Promise<AdminArmProjectsResponse> {
  return request<AdminArmProjectsResponse>(
    `/api/admin/experimentation/${encodeURIComponent(key)}/arms/${encodeURIComponent(arm)}/projects`,
  );
}

/** Every arm this project is in, for EVERY kind, from `project_arms`. The
 *  project-unit twin of the exposures on `/projects/{id}`, which answers the
 *  same question for the USER unit and so only for the two product cohorts.
 *  `system_admin` at READ. */
export function fetchProjectArms(projectId: string): Promise<AdminProjectArmsResponse> {
  return request<AdminProjectArmsResponse>(
    `/api/admin/experimentation/projects/${encodeURIComponent(projectId)}/arms`,
  );
}

/** Every tagger version and where it sits on the trust ladder. `system_admin` at READ. */
export function fetchTagVersions(): Promise<AdminTagVersionsResponse> {
  return request<AdminTagVersionsResponse>("/api/admin/experimentation/tags");
}

/** Move one tagger version along the trust ladder. The route refuses `sentinel`
 *  unless the version is already trusted, because a sentinel tag becomes a live
 *  injection trigger the moment it lands. `system_admin` at FULL. */
export function setTagState(
  versionId: string,
  body: SetTagStateRequest,
): Promise<AdminTagStateResponse> {
  return request<AdminTagStateResponse>(
    `/api/admin/experimentation/tags/${encodeURIComponent(versionId)}/state`,
    jsonBody(body),
  );
}

/** The trigger vocabulary: every name a turn's fired set may hold, where it
 *  comes from and when it is known relative to the turn. `system_admin` at READ. */
export function fetchExperimentationTriggers(): Promise<AdminTriggersResponse> {
  return request<AdminTriggersResponse>("/api/admin/experimentation/triggers");
}

export function fetchAdminBudgetThresholds(): Promise<AdminBudgetThresholdsResponse> {
  return request<AdminBudgetThresholdsResponse>("/api/admin/budget-thresholds");
}

/** Move one committed-budget tier (whole USD). `system_admin` at FULL: this
 *  decides which clients get handed to a human desk and offered the Calendly
 *  scheduler. Returns the refreshed list. */
export function setBudgetThreshold(
  key: string,
  usd: number,
  reason: string,
): Promise<AdminBudgetThresholdsResponse> {
  return request<AdminBudgetThresholdsResponse>(
    `/api/admin/budget-thresholds/${encodeURIComponent(key)}`,
    jsonBody({ usd, reason }),
  );
}

/** One experiment's arms across the funnel steps. Requires `version_cohorts` —
 *  shared with Version Cohorts, which asks the same question across a different
 *  boundary; aggregate-only, so it carries the same grant for the same reason. */
export function fetchAbTests(
  experiment: string | undefined,
  window: AbPresetWindow,
): Promise<AdminAbTestsEnvelope> {
  const params = new URLSearchParams({ window });
  if (experiment) params.set("experiment", experiment);
  return request<AdminAbTestsEnvelope>(`/api/admin/ab-tests?${params}`);
}

/** The same readout over an operator-chosen cohort range, computed on demand.
 *
 *  The four presets are materialized together by a worker tick and read straight
 *  from cache (`fetchAbTests` above). A range nobody could precompute is computed
 *  when asked for: the route answers `computing` and enqueues, and the caller
 *  polls until `ready`. Same envelope as the preset lane, so the page renders one
 *  shape either way.
 *
 *  The EXPERIMENT rides in the request because it is part of the range's identity
 *  server-side: a readout carries only the selected experiment's KPIs. */
export function fetchAbTestsRange(
  experiment: string | undefined,
  range: DateRange,
): Promise<AdminAbTestsEnvelope> {
  const params = new URLSearchParams();
  appendDateRange(params, range);
  if (experiment) params.set("experiment", experiment);
  const qs = params.toString();
  return request<AdminAbTestsEnvelope>(`/api/admin/ab-tests/range${qs ? `?${qs}` : ""}`);
}

/** Every recorded deploy in this environment, plus each metric rolled up either
 *  side of the chosen one. Requires `version_cohorts`; aggregate-only, like the
 *  Funnel. `release` omitted means the newest recorded deploy. */
export function fetchVersionCohorts(
  window: CohortWindow,
  release?: string,
): Promise<AdminVersionCohortsResponse> {
  const params = new URLSearchParams({ window });
  if (release) params.set("release", release);
  return request<AdminVersionCohortsResponse>(`/api/admin/version-cohorts?${params}`);
}

/** The shortlist-timeframe readout (Monday 3204302464): what happened, by the
 *  timeframe the buyer chose at the start-choice picker, rolled up over the last
 *  `window` days. A pure cache read like the cohorts above — the worker
 *  materializes the daily series every fifteen minutes and the route divides. */
export function fetchShortlistTimeframe(
  window: TimeframeWindow,
): Promise<AdminShortlistTimeframeResponse> {
  const params = new URLSearchParams({ window });
  return request<AdminShortlistTimeframeResponse>(`/api/admin/shortlist-timeframe?${params}`);
}

/** The same page narrowed to ONE device/country cohort, computed on demand.
 *
 *  A separate route from the read above because it is a different kind of answer:
 *  the whole-population series is materialized by a worker tick and read from
 *  cache, while a cohort nobody could precompute is built when asked for. So this
 *  one can legitimately come back `computing` with no cohorts, and the caller polls
 *  until it is `ready` (`versionCohortsSliceQuery` owns that bound). Both axes are
 *  sent sorted so two spellings of one cohort cannot become two computes. */
export function fetchVersionCohortsSlice(
  window: CohortWindow,
  release: string | undefined,
  devices: readonly string[],
  countries: readonly string[],
): Promise<AdminVersionCohortsSliceResponse> {
  const params = new URLSearchParams({ window });
  if (release) params.set("release", release);
  for (const device of [...devices].sort()) params.append("devices", device);
  for (const country of [...countries].sort()) params.append("countries", country);
  return request<AdminVersionCohortsSliceResponse>(`/api/admin/version-cohorts/slice?${params}`);
}

/** Every origin country the store holds, most common first, with project counts:
 *  the vocabulary the country filters list (the set is OPEN, so it cannot be a
 *  constant). Plain employee scope; counts only. */
export function fetchAdminCountries(): Promise<AdminCountriesResponse> {
  return request<AdminCountriesResponse>("/api/admin/countries");
}

/** The launch board narrowed to ONE A/B group.
 *
 *  A three-state envelope, not a board: the scoped pass runs the same
 *  whole-population statements the scheduled refresh runs, so it is built by the
 *  worker and this answers `computing` while that happens. `unavailable` is a
 *  different answer entirely and must not be rendered as an empty board.
 *
 *  EMPLOYEE session only (`launch_day`), deliberately not the kiosk token: the
 *  TV read is a capability for the whole-population aggregate. */
export function fetchLaunchDashboardArm(
  scope: ExperimentScope,
): Promise<LaunchDashboardArmResponse> {
  const params = new URLSearchParams({ experiment: scope.experiment, arm: scope.arm });
  return request<LaunchDashboardArmResponse>(`/api/admin/launch/dashboard/arm?${params}`);
}

export function fetchLaunchDashboard(token?: string): Promise<LaunchDashboardResponse> {
  return request<LaunchDashboardResponse>(
    "/api/admin/launch/dashboard",
    token ? { headers: { "x-launch-token": token } } : {},
  );
}

/** The rows behind one launch tile, for the click-through modal. EMPLOYEE
 *  session only: this response names users and projects, so the kiosk's
 *  remote-view token is deliberately not accepted (the kiosk renders the same
 *  tiles unclickable). */
export function fetchLaunchDrilldown(
  metric: LaunchDrilldownMetric,
  window: LaunchDrilldownWindow,
): Promise<LaunchDrilldownResponse> {
  const params = new URLSearchParams({ metric, window });
  return request<LaunchDrilldownResponse>(`/api/admin/launch/drilldown?${params}`);
}

/** Mint a ONE-TIME code (system_admin only) someone types into the TV computer. */
export function mintLaunchRemoteLink(): Promise<LaunchRemoteLinkResponse> {
  return request<LaunchRemoteLinkResponse>("/api/admin/launch/remote-link", { method: "POST" });
}

/** Exchange a typed-in code for the short-lived read-only viewing token.
 *  Unauthenticated by design: the code IS the credential. */
export function redeemLaunchCode(code: string): Promise<LaunchRedeemResponse> {
  return request<LaunchRedeemResponse>("/api/admin/launch/redeem", jsonBody({ code }));
}

// --- Debug notes (the console's own tester Debug toggle) --------------------

/** File a tester debug note FROM the admin console (the sidebar Debug toggle),
 *  with the optional auto-captured html2canvas screenshot. Lands in the same
 *  `debug_notes` review tab as buyer-SPA notes, attributed to the signed-in
 *  employee, with the admin `page` recorded so the reviewer knows where it was
 *  filed. Multipart on purpose (it carries an image); the browser sets the
 *  boundary, so no explicit content-type. 404 in prod (tester tool).
 *
 *  The screenshot is a best-effort extra, never the note's ransom: capture is
 *  uncapped client-side (a large viewport can exceed the server's byte cap), and
 *  the server 413s the WHOLE request over the cap — so on 413 the note is
 *  re-posted once without the image rather than losing the tester's text. */
export async function createAdminDebugNote(input: {
  note: string;
  page: string | null;
  screenshot: Blob | null;
}): Promise<{ id: string }> {
  const post = (screenshot: Blob | null) => {
    const form = new FormData();
    form.set("note", input.note);
    if (input.page) form.set("page", input.page);
    if (screenshot) form.set("screenshot", screenshot, "screenshot.jpg");
    return request<{ id: string }>("/api/admin/debug-notes", { method: "POST", body: form });
  };
  try {
    return await post(input.screenshot);
  } catch (error) {
    if (input.screenshot && error instanceof ApiError && error.status === 413) {
      return post(null);
    }
    throw error;
  }
}
